// src/shared/components/MessageInstructorModal.tsx
//
// A small contact form, opened from the AI-Coach mentor panel's "Message {name}"
// button. The logged-in student types a subject + message; on submit it POSTs to
// /api/instructor-messages, which stores it in the instructor panel's Messages
// inbox AND emails the configured instructor address. Name/email come from the
// session server-side, so the form only asks for subject + message.
//
// Editorial ("Operational Editorial") grammar to match the mentor panel: flat
// white card, hairline border, mono eyebrow, serif title, accent send button.
import { useState } from "react";
import { createPortal } from "react-dom";
import { X, Send, CheckCircle2 } from "lucide-react";
import { supabase } from "@/shared/lib/supabase";
import { useT } from "@/shared/i18n/useT";

export function MessageInstructorModal({
  instructorName,
  onClose,
}: {
  instructorName: string;
  onClose: () => void;
}) {
  const t = useT();
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const body = message.trim();
    if (!body || sending) return;
    setSending(true);
    setError(null);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const res = await fetch("/api/instructor-messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ subject: subject.trim(), body }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.error)
        throw new Error(data.error || "Failed to send");
      setSent(true);
    } catch {
      setError(t("shell.miError"));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full border border-[var(--hairline)] bg-white px-3 py-2 text-[13.5px] text-[var(--fg)] placeholder-[var(--muted)] outline-none transition-colors focus:border-[var(--accent)]";

  return createPortal(
    <div
      className="app-shell fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white border border-[var(--hairline)] shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 px-5 pt-5">
          <div>
            <p className="ed-mono text-[10px] text-[var(--muted)] mb-1.5">
              {t("shell.mentor")}
            </p>
            <h2 className="ed-serif text-[20px] text-[var(--ink-2)]">
              {t("shell.miTitle", { name: instructorName })}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 -mr-1 -mt-1 p-1.5 text-[var(--muted)] hover:text-[var(--ink-2)] transition-colors cursor-pointer"
            aria-label={t("shell.miClose")}
          >
            <X size={18} />
          </button>
        </div>

        {sent ? (
          <div className="px-5 py-10 text-center">
            <CheckCircle2 className="w-10 h-10 mx-auto text-[var(--accent)] mb-3" />
            <p className="text-[14px] text-[var(--fg)]">{t("shell.miSent")}</p>
            <button
              type="button"
              onClick={onClose}
              className="mt-5 inline-flex items-center justify-center border border-[var(--hairline)] bg-white px-4 py-2 text-[13px] text-[var(--ink-2)] hover:bg-[color-mix(in_srgb,var(--accent)_6%,#fff)] transition-colors cursor-pointer"
            >
              {t("shell.miClose")}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-5 pb-5 pt-3 space-y-3">
            <p className="text-[13px] text-[var(--muted)] leading-relaxed">
              {t("shell.miSubtitle")}
            </p>
            <div>
              <label className="ed-mono block text-[10px] text-[var(--muted)] mb-1.5">
                {t("shell.miSubject")}
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={t("shell.miSubjectPlaceholder")}
                className={inputCls}
              />
            </div>
            <div>
              <label className="ed-mono block text-[10px] text-[var(--muted)] mb-1.5">
                {t("shell.miMessage")}
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t("shell.miMessagePlaceholder")}
                rows={5}
                required
                className={`${inputCls} resize-none`}
              />
            </div>
            {error && <p className="text-[13px] text-red-600">{error}</p>}
            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-[13px] text-[var(--muted)] hover:text-[var(--ink-2)] transition-colors cursor-pointer"
              >
                {t("shell.miCancel")}
              </button>
              <button
                type="submit"
                disabled={sending || !message.trim()}
                className="inline-flex items-center gap-2 bg-[var(--ink)] px-4 py-2 text-[13px] font-medium text-white transition-opacity hover:opacity-90 active:scale-[0.98] disabled:opacity-50 cursor-pointer"
              >
                {sending ? (
                  t("shell.miSending")
                ) : (
                  <>
                    <Send size={14} />
                    {t("shell.miSend")}
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body,
  );
}
